import React from 'react';
import PropTypes from 'prop-types';
import { bemGenerator } from '@prisjakt/ui-base';
import dayjs from 'dayjs';
import './flip-clock.scss';

const styles = bemGenerator('ui-flip-clock', [
  'unit-outer',
  'unit-wrapper',
  'unit-container',
  'unit',
  'label',
]);

const UPDATE_FREQ_MS = 100;

const getDigits = (n, digits) =>
  `${n}`
    .padStart(digits, 0)
    .substr(-digits)
    .split('')
    .map(digit => parseInt(digit, 10));
const naiveEqual = (a, b) => Object.keys(a).every(key => a[key] === b[key]);
const getSecondsDifference = (target, current) =>
  Math.round((target.getTime() - current.getTime()) / 1000);
const getTimeFromSeconds = timeInSeconds => {
  const days = Math.floor(timeInSeconds / (60 * 60 * 24));
  const divisorHours = timeInSeconds % (60 * 60 * 24);
  const hours = Math.floor(divisorHours / (60 * 60));
  const divisorMinutes = timeInSeconds % (60 * 60);
  const minutes = Math.floor(divisorMinutes / 60);
  const divisorSeconds = divisorMinutes % 60;
  const seconds = Math.ceil(divisorSeconds);
  return {
    days,
    hours,
    minutes,
    seconds,
  };
};

const FlipUnit = ({ position, digit, animation }) => (
  <div
    className={styles.unit.withModifier({
      [`position-${position}`]: true,
      [`animation-${animation}`]: true,
    })}
  >
    <span>{digit}</span>
  </div>
);

FlipUnit.propTypes = {
  position: PropTypes.string.isRequired,
  digit: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  animation: PropTypes.string,
};

FlipUnit.defaultProps = {
  animation: 'none',
};

const FlipUnitContainer = ({ previous, current }) => {
  const shuffle = !!(current % 2);
  // shuffle digits
  const digit1 = shuffle ? previous : current;
  const digit2 = !shuffle ? previous : current;
  // shuffle animations
  const anim1 = shuffle ? 'in' : 'out';
  const anim2 = !shuffle ? 'in' : 'out';
  return (
    <div className={styles['unit-container']}>
      <FlipUnit position="upper" digit={current} />
      <FlipUnit position="lower" digit={previous} />
      <FlipUnit position="first" digit={digit1} animation={anim1} />
      <FlipUnit position="second" digit={digit2} animation={anim2} />
    </div>
  );
};

FlipUnitContainer.propTypes = {
  previous: PropTypes.number.isRequired,
  current: PropTypes.number.isRequired,
};

class FlipClock extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      previous: this.getState(new Date(Date.now() - 1000)),
      current: this.getState(),
    };

    this.lastUpdate = 0;
  }

  componentDidMount() {
    this.updateRequest = window.requestAnimationFrame(this.update.bind(this));
  }

  componentWillUnmount() {
    window.cancelAnimationFrame(this.updateRequest);
  }

  getState(current = new Date()) {
    const target = new Date(dayjs(this.props.targetDate || 0));
    return getTimeFromSeconds(
      Math.max(0, getSecondsDifference(target, current))
    );
  }

  update(time) {
    if (time - this.lastUpdate > UPDATE_FREQ_MS) {
      this.lastUpdate = time;
      const { current } = this.state;
      const next = this.getState();
      if (!naiveEqual(current, next)) {
        this.setState({ previous: current, current: next });
      }
    }
    this.updateRequest = window.requestAnimationFrame(this.update.bind(this));
  }

  static renderInvalid(digits, char = '-') {
    return new Array(digits).fill(char).map((c, i) => (
      // eslint-disable-next-line react/no-array-index-key
      <div key={`invalid-${i}`} className={styles['unit-container']}>
        <FlipUnit position="upper" digit={c} />
        <FlipUnit position="lower" digit={c} />
      </div>
    ));
  }

  static renderDigits(previous, current, digits) {
    if (isNaN(previous) || isNaN(current)) {
      return FlipClock.renderInvalid(digits);
    }

    const digitArray = [
      getDigits(previous, digits),
      getDigits(current, digits),
    ];

    const result = [];
    for (let i = 0; i < digits; i += 1) {
      result.push(
        <FlipUnitContainer
          key={i}
          previous={digitArray[0][i]}
          current={digitArray[1][i]}
        />
      );
    }
    return result;
  }

  render() {
    const { previous, current } = this.state;
    const {
      size,
      daysLabel,
      hoursLabel,
      minutesLabel,
      secondsLabel,
    } = this.props;

    return (
      <div className={styles.block.withModifier({ [`size-${size}`]: true })}>
        <div className={styles['unit-outer']}>
          <div className={styles['unit-wrapper']}>
            {FlipClock.renderDigits(previous.days, current.days, 3)}
          </div>
          {daysLabel && <div className={styles.label}>{daysLabel}</div>}
        </div>
        <div className={styles['unit-outer']}>
          <div className={styles['unit-wrapper']}>
            {FlipClock.renderDigits(previous.hours, current.hours, 2)}
          </div>
          {hoursLabel && <div className={styles.label}>{hoursLabel}</div>}
        </div>
        <div className={styles['unit-outer']}>
          <div className={styles['unit-wrapper']}>
            {FlipClock.renderDigits(previous.minutes, current.minutes, 2)}
          </div>
          {minutesLabel && <div className={styles.label}>{minutesLabel}</div>}
        </div>
        <div className={styles['unit-outer']}>
          <div className={styles['unit-wrapper']}>
            {FlipClock.renderDigits(previous.seconds, current.seconds, 2)}
          </div>
          {secondsLabel && <div className={styles.label}>{secondsLabel}</div>}
        </div>
      </div>
    );
  }
}

FlipClock.propTypes = {
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  targetDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  daysLabel: PropTypes.string,
  hoursLabel: PropTypes.string,
  minutesLabel: PropTypes.string,
  secondsLabel: PropTypes.string,
};

FlipClock.defaultProps = {
  size: 'medium',
  daysLabel: undefined,
  hoursLabel: undefined,
  minutesLabel: undefined,
  secondsLabel: undefined,
};

export default FlipClock;
